import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Trash, UploadSimple, Palette, Storefront, Printer } from "@phosphor-icons/react";
import { api, API } from "../../lib/api";

const TIERS = [
  { v: "community", label: "Community (map rotation)" },
  { v: "supporter", label: "Supporter (map + incident panel)" },
  { v: "presenting", label: "Presenting (everything + snapshot card)" },
];

const fmtCount = (n) => {
  if (!n) return "0";
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
};

const monthKey = () => new Date().toISOString().slice(0, 7);

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtMonth = (k) => {
  const [y, m] = k.split("-");
  return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`;
};

// Print-ready per-sponsor season report (for renewals)
function printSponsorReport(s) {
  const months = Object.entries(s.stats || {}).sort(([a], [b]) => a.localeCompare(b));
  const rows = months
    .map(
      ([m, v]) =>
        `<tr><td>${fmtMonth(m)}</td><td>${(v.impressions || 0).toLocaleString()}</td><td>${(v.clicks || 0).toLocaleString()}</td></tr>`
    )
    .join("");
  const logo = s.has_logo ? `<img src="${API}/sponsors/${s.id}/logo" style="height:64px;object-fit:contain"/>` : "";
  const tierLabel = (TIERS.find((t) => t.v === s.tier) || {}).label || s.tier || "";
  const since = s.created_at ? new Date(s.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "—";
  const today = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  const html = `<!doctype html><html><head><title>Sponsor Season Report — ${s.name}</title>
<style>
  body{font-family:Georgia,'Times New Roman',serif;color:#1a1a1a;max-width:720px;margin:40px auto;padding:0 24px}
  .head{display:flex;justify-content:space-between;align-items:center;border-bottom:3px solid #F2542D;padding-bottom:16px}
  h1{font-size:22px;margin:0}
  .sub{color:#666;font-size:13px;margin-top:4px}
  .brand{font-size:12px;letter-spacing:2px;text-transform:uppercase;color:#F2542D;font-weight:bold}
  .totals{display:flex;gap:24px;margin:28px 0}
  .tot{border:1px solid #ddd;border-radius:4px;padding:14px 22px;text-align:center;flex:1}
  .tot b{display:block;font-size:30px}
  .tot span{font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#777}
  table{width:100%;border-collapse:collapse;margin-top:8px}
  th{font-size:11px;text-transform:uppercase;letter-spacing:1px;text-align:left;border-bottom:2px solid #333;padding:6px 8px}
  td{padding:7px 8px;border-bottom:1px solid #e5e5e5;font-size:14px}
  tr:nth-child(even) td{background:#fafafa}
  .foot{margin-top:36px;font-size:11px;color:#888;border-top:1px solid #ddd;padding-top:12px}
  @media print{body{margin:0 auto}}
</style></head><body>
  <div class="head">
    <div>
      <div class="brand">Okanogan Highlands Firewatch</div>
      <h1>Sponsor Season Report — ${s.name}</h1>
      <div class="sub">${tierLabel} · Sponsor since ${since} · Generated ${today}</div>
    </div>
    ${logo}
  </div>
  <div class="totals">
    <div class="tot"><b>${(s.impressions || 0).toLocaleString()}</b><span>Total badge views</span></div>
    <div class="tot"><b>${(s.clicks || 0).toLocaleString()}</b><span>Clicks to your site</span></div>
    <div class="tot"><b>${months.length}</b><span>Months on the map</span></div>
  </div>
  <table>
    <thead><tr><th>Month</th><th>Badge views</th><th>Clicks</th></tr></thead>
    <tbody>${rows || '<tr><td colspan="3">No monthly data yet</td></tr>'}</tbody>
  </table>
  <div class="foot">
    Views are counted each time the sponsor badge is displayed on the live fire map or incident pages.
    Thank you for keeping community wildfire information free for everyone.
  </div>
  <script>window.onload=function(){setTimeout(function(){window.print()},500)}</scr` + `ipt>
</body></html>`;
  const w = window.open("", "_blank", "width=820,height=940");
  if (!w) return;
  w.document.write(html);
  w.document.close();
}


export default function SponsorsAdmin() {
  return (
    <div className="space-y-6 mt-8">
      <SponsorManager />
      <ArtworkManager />
    </div>
  );
}

function SponsorManager() {
  const [sponsors, setSponsors] = useState([]);
  const [name, setName] = useState("");
  const [link, setLink] = useState("");
  const [tier, setTier] = useState("community");
  const [logoFile, setLogoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/sponsors/all").then((r) => setSponsors(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", name.trim());
      fd.append("link_url", link.trim());
      fd.append("tier", tier);
      if (logoFile) fd.append("logo", logoFile);
      await api.post("/sponsors", fd);
      toast.success("Sponsor added");
      setName(""); setLink(""); setTier("community"); setLogoFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add sponsor");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (s) => {
    const fd = new FormData();
    fd.append("active", String(!s.active));
    await api.put(`/sponsors/${s.id}`, fd).then(load).catch(() => toast.error("Update failed"));
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete sponsor "${s.name}"?`)) return;
    await api.delete(`/sponsors/${s.id}`).then(load).catch(() => toast.error("Delete failed"));
  };

  return (
    <section className="border border-tac-border bg-tac-surface/40" data-testid="sponsors-admin">
      <div className="px-4 py-3 border-b border-tac-border flex items-center gap-2">
        <Storefront size={15} weight="duotone" className="text-tac-primary" />
        <span className="text-[15px] uppercase tracking-[0.15em] font-bold text-tac-faint">Sponsors</span>
        <span className="ml-auto text-[10px] font-mono-tac text-tac-faint">{sponsors.filter((s) => s.active).length} active</span>
      </div>

      <form onSubmit={add} className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 border-b border-tac-border/60">
        <input
          data-testid="sponsor-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Business / sponsor name *"
          className="bg-tac-surface border border-tac-border focus:border-tac-primary rounded-sm px-3 py-2 text-[13px] text-tac-text placeholder:text-tac-faint outline-none"
        />
        <input
          data-testid="sponsor-link-input"
          value={link}
          onChange={(e) => setLink(e.target.value)}
          placeholder="Website or Facebook link (optional)"
          className="bg-tac-surface border border-tac-border focus:border-tac-primary rounded-sm px-3 py-2 text-[13px] text-tac-text placeholder:text-tac-faint outline-none"
        />
        <select
          data-testid="sponsor-tier-select"
          value={tier}
          onChange={(e) => setTier(e.target.value)}
          className="bg-tac-surface border border-tac-border rounded-sm px-3 py-2 text-[13px] text-tac-text outline-none"
        >
          {TIERS.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
        </select>
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            data-testid="sponsor-logo-input"
            type="file"
            accept="image/*"
            onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
            className="text-[11px] text-tac-dim file:mr-2 file:px-2 file:py-1 file:border file:border-tac-border file:bg-tac-surface file:text-tac-text file:rounded-sm file:text-[10px] file:uppercase file:font-bold"
          />
          <button
            data-testid="sponsor-add-btn"
            type="submit"
            disabled={saving || !name.trim()}
            className="ml-auto px-4 py-2 text-[10px] font-bold uppercase tracking-wider bg-tac-primary text-white rounded-sm hover:bg-tac-primary/90 disabled:opacity-40 whitespace-nowrap"
          >
            {saving ? "Saving…" : "Add Sponsor"}
          </button>
        </div>
      </form>

      <div className="divide-y divide-tac-border/60">
        {sponsors.length === 0 && (
          <div className="px-4 py-4 text-[12px] text-tac-faint">No sponsors yet — add your first one above.</div>
        )}
        {sponsors.map((s) => (
          <div key={s.id} className="px-4 py-2.5 flex items-center gap-3" data-testid={`sponsor-row-${s.id}`}>
            {s.has_logo && <img src={`${API}/sponsors/${s.id}/logo`} alt="" className="h-7 w-14 object-contain shrink-0" />}
            <div className="min-w-0">
              <div className="text-[13px] font-bold text-tac-text truncate">{s.name}</div>
              <div className="text-[10px] font-mono-tac text-tac-faint uppercase">{s.tier}{s.link_url ? " · linked" : ""}</div>
              <div className="text-[10px] font-mono-tac text-tac-dim" data-testid={`sponsor-stats-${s.id}`}>
                {fmtCount(s.impressions)} views · {fmtCount(s.clicks)} clicks
                {s.stats?.[monthKey()] ? ` · this month: ${fmtCount(s.stats[monthKey()].impressions)} / ${fmtCount(s.stats[monthKey()].clicks)}` : ""}
              </div>
            </div>
            <label className="ml-auto flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-tac-dim cursor-pointer">
              <input type="checkbox" checked={s.active} onChange={() => toggle(s)} data-testid={`sponsor-active-${s.id}`} className="accent-tac-primary" />
              Active
            </label>
            <button onClick={() => printSponsorReport(s)} data-testid={`sponsor-report-${s.id}`} title="Print season report" className="text-tac-dim hover:text-tac-primary p-1">
              <Printer size={14} weight="bold" />
            </button>
            <button onClick={() => remove(s)} data-testid={`sponsor-delete-${s.id}`} className="text-tac-danger/70 hover:text-tac-danger p-1">
              <Trash size={14} />
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

function ArtworkManager() {
  const [artworks, setArtworks] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/artworks").then((r) => setArtworks(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("label", f.name.replace(/\.[^.]+$/, ""));
      fd.append("file", f);
      await api.post("/artworks", fd);
      toast.success("Artwork added to the rotation");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const toggle = async (a) => {
    const fd = new FormData();
    fd.append("active", String(!a.active));
    await api.put(`/artworks/${a.id}`, fd).then(load).catch(() => toast.error("Update failed"));
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete artwork "${a.label}"?`)) return;
    await api.delete(`/artworks/${a.id}`).then(load).catch(() => toast.error("Delete failed"));
  };

  const activeCount = artworks.filter((a) => a.active).length;

  return (
    <section className="border border-tac-border bg-tac-surface/40" data-testid="artworks-admin">
      <div className="px-4 py-3 border-b border-tac-border flex items-center gap-2">
        <Palette size={15} weight="duotone" className="text-tac-info" />
        <span className="text-[15px] uppercase tracking-[0.15em] font-bold text-tac-faint">Daily Snapshot Artwork</span>
        <span className="ml-auto text-[10px] font-mono-tac text-tac-faint">
          {activeCount ? `${activeCount} in rotation · a different one each day` : "using default artwork"}
        </span>
      </div>
      <div className="p-4">
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="artwork-file-input" />
        <button
          data-testid="artwork-upload-btn"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-2 px-4 py-2 text-[10px] font-bold uppercase tracking-wider border border-dashed border-tac-border rounded-sm text-tac-dim hover:border-tac-borderBright hover:text-tac-text transition-colors disabled:opacity-50"
        >
          <UploadSimple size={13} weight="bold" />
          {uploading ? "Uploading…" : "Upload artwork"}
        </button>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          {artworks.map((a) => (
            <div key={a.id} className="border border-tac-border rounded-sm overflow-hidden" data-testid={`artwork-card-${a.id}`}>
              <img src={`${API}/artworks/${a.id}/image`} alt={a.label} className={`w-full h-24 object-cover ${a.active ? "" : "opacity-40 grayscale"}`} />
              <div className="px-2 py-1.5 flex items-center gap-2">
                <span className="text-[10px] text-tac-dim truncate flex-1">{a.label}</span>
                <input type="checkbox" checked={a.active} onChange={() => toggle(a)} data-testid={`artwork-active-${a.id}`} className="accent-tac-info" title="In rotation" />
                <button onClick={() => remove(a)} data-testid={`artwork-delete-${a.id}`} className="text-tac-danger/70 hover:text-tac-danger">
                  <Trash size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
